// src/hooks/useMap.js
// The only hook components use to interact with the map.
// Initializes Leaflet, mounts all layers, wires all events.
// Components receive refs and callbacks — no Leaflet imports needed.
//
// Usage:
//   const { containerRef, mapReady, activeMode, setMode } = useMap({ incidents, shelters, ... });
//   return <div ref={containerRef} style={{ flex:1 }} />;

import { useEffect, useRef, useState, useCallback } from 'react';
import { MapManager, ICONS }               from '@services/maps/leafletCore';
import { OverlayManager, startRadarRefresh,
         stopRadarRefresh, renderUserLocation,
         renderEvacRoutes, renderFloodZonePolygons } from '@services/maps/overlays';
import { initLayers, renderIncidents, renderIncidentsClustered, renderSOS,
         renderShelters, renderGasStations,
         renderUSGSGauges, renderHospitals,
         renderConnectivity,
         setVisibleGroups }                from '@services/maps/clustering';
import { attachMapEvents, detachMapEvents } from '@services/maps/events';
import { prefetchViewportTiles }            from '@services/maps/tileCache';
import { consumeRateLimit }                  from '@services/rateLimit';
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
import { MAP_MODES, ZOOM_THRESHOLDS,
         DEFAULT_PROVIDER, TILE_PROVIDERS } from '@services/maps/config';
import { useMapStore }                     from '@store';

export function useMap({
  incidents  = [],
  sosReports = [],
  shelters   = [],
  gasStations= [],
  gauges     = [],
  hospitals  = [],
  evacRoutes        = [],
  floodZones        = [],
  connectivityPoints = [],
  userLat           = null,
  userLon    = null,
  userAccuracy = null,
  onSelectItem,         // callback when user taps a marker
  onBoundsChange,       // callback when map viewport changes
  onZoomChange,         // callback when zoom level changes
} = {}) {
  const containerRef = useRef(null);
  const [mapReady,   setMapReady]   = useState(false);
  const [zoom,       setZoom]       = useState(12);

  // Mode from global store so other parts of the app can switch the map
  const mapMode  = useMapStore((s) => s.mapMode);
  const setMapMode = useMapStore((s) => s.setMapMode);

  // ── Initialize map ─────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const mapRenderStart = performance.now();
    const map = MapManager.init(el);
    // Report map render time once after first render
    map.once?.('render', () => {
      const renderMs = Math.round(performance.now() - mapRenderStart);
      // Write to SW/storage for command center to pick up
      try {
        const existing = JSON.parse(localStorage.getItem('eos-map-perf') ?? '{}');
        localStorage.setItem('eos-map-perf', JSON.stringify({
          ...existing,
          lastRenderMs: renderMs,
          lastRenderAt: Date.now(),
          avgRenderMs:  existing.avgRenderMs
            ? Math.round((existing.avgRenderMs * 0.8) + (renderMs * 0.2))
            : renderMs,
        }));
      } catch {}
    });
    initLayers(map);

    // Debounce bounds notifications — wait 400ms after movement stops
    // before refiltering pins. Prevents rerender on every pixel of drag.
    const notifyBoundsChange = debounce(() => {
      const b = map.getBounds?.();
      if (!b || !onBoundsChange) return;
      onBoundsChange({
        north: b.getNorth(), south: b.getSouth(),
        east:  b.getEast(),  west:  b.getWest(),
      });
    }, 400);

    const cleanup = attachMapEvents(map, {
      onMove: ({ zoom: z }) => { setZoom(z); notifyBoundsChange(); onZoomChange?.(z); },
      onZoom: ({ zoom: z }) => { setZoom(z); notifyBoundsChange(); onZoomChange?.(z); },
    });

    // Seed initial bounds immediately so the first render shows only
    // viewport-relevant pins rather than the full snapshot.
    // Call directly (no debounce) — the map is already settled at init.
    const initBounds = map.getBounds?.();
    if (initBounds && onBoundsChange) {
      onBoundsChange({
        north: initBounds.getNorth(), south: initBounds.getSouth(),
        east:  initBounds.getEast(),  west:  initBounds.getWest(),
      });
    }

    setMapReady(true);

    return () => {
      cleanup();
      detachMapEvents(map);
      MapManager.destroy();
      setMapReady(false);
    };
  }, []);  // runs once

  // ── Apply mode (overlays + visible groups) ─────────────────────
  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady) return;

    const mode = MAP_MODES.find((m) => m.id === mapMode);
    if (!mode) return;

    // Tile overlays for this mode
    OverlayManager.setActive(map, mode.layers);

    // Start/stop radar refresh
    if (mode.layers.includes('noaa_radar')) {
      startRadarRefresh(map);
    } else {
      stopRadarRefresh();
    }

    // Marker groups visible in each mode
    const visibleGroups = {
      situation:    [],
      flood:        ['gauges', 'incidents'],
      roads:        ['incidents'],
      power:        ['incidents'],
      medical:      ['hospitals'],
      radar:        [],
      shelters:     ['shelters'],
      gas:          ['gas'],
      evac:         [],
      connectivity: ['connectivity'],
    };
    setVisibleGroups(map, visibleGroups[mapMode] ?? []);

    // Evac routes — render polylines (empty unless we have coordinates)
    if (mapMode === 'evac') {
      renderEvacRoutes(map, evacRoutes, onSelectItem);
    }

    // Flood zones — render FEMA polygons (when available from seed/API)
    if (mapMode === 'flood' && floodZones.length > 0) {
      renderFloodZonePolygons(map, floodZones);
    }
  }, [mapMode, mapReady]);  // re-runs on mode switch

  // ── Re-render markers when data changes ───────────────────────
  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady) return;
    renderIncidentsClustered(map, incidents, onSelectItem, zoom);
  }, [incidents, mapReady, zoom, onSelectItem]);

  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady) return;
    renderSOS(map, sosReports, onSelectItem);
  }, [sosReports, mapReady]);

  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady) return;
    renderShelters(map, shelters, onSelectItem);
  }, [shelters, mapReady]);

  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady) return;
    renderGasStations(map, gasStations, onSelectItem);
  }, [gasStations, mapReady]);

  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady) return;
    renderUSGSGauges(map, gauges, onSelectItem);
  }, [gauges, mapReady]);

  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady) return;
    renderHospitals(map, hospitals, onSelectItem);
  }, [hospitals, mapReady]);

  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady) return;
    renderConnectivity(map, connectivityPoints, onSelectItem);
  }, [connectivityPoints, mapReady]);

  // ── User location ─────────────────────────────────────────────
  useEffect(() => {
    const map = MapManager.get();
    if (!map || !mapReady || !userLat || !userLon) return;
    renderUserLocation(map, userLat, userLon, userAccuracy ?? 50, ICONS);
  }, [userLat, userLon, userAccuracy, mapReady]);

  // ── Offline tile caching (on move-end, while online) ──────────
  useEffect(() => {
    if (!mapReady) return;
    const handler = () => {
      if (!navigator.onLine) return;
      const map = MapManager.get();
      if (!map) return;
      // Rate limit: max 1 viewport tile prefetch per 30 seconds
      if (consumeRateLimit('mapRefresh').allowed) {
        prefetchViewportTiles(map.getBounds()).catch(() => {});
      }
    };
    window.addEventListener('na-map-moved', handler);
    return () => window.removeEventListener('na-map-moved', handler);
  }, [mapReady]);

  // ── Programmatic navigation ────────────────────────────────────
  const flyTo = useCallback((lat, lon, z) => {
    MapManager.flyTo(lat, lon, z);
  }, []);

  const fitBounds = useCallback((bounds) => {
    MapManager.fitBounds(bounds);
  }, []);

  // ── Zoom controls ──────────────────────────────────────────────
  const zoomIn  = useCallback(() => { MapManager.get()?.zoomIn();  }, []);
  const zoomOut = useCallback(() => { MapManager.get()?.zoomOut(); }, []);

  // ── Provider switching ─────────────────────────────────────────
  const setProvider = useCallback((providerId) => {
    const p = TILE_PROVIDERS[providerId] ?? DEFAULT_PROVIDER;
    MapManager.setProvider(p);
  }, []);

  return {
    containerRef,
    mapReady,
    zoom,
    activeMode:  mapMode,
    modes:       MAP_MODES,
    setMode:     setMapMode,
    flyTo,
    fitBounds,
    zoomIn,
    zoomOut,
    setProvider,
    providers:   TILE_PROVIDERS,
  };
}
